<template>
  <StoreBreadcrumb :items="breadcrumb"  />  

    <div v-if="pending && !product" class="container mx-auto px-4 py-12">
      <USkeleton class="h-[500px] w-full rounded-lg" />
      <USkeleton class="h-[500px] w-full rounded-lg" />
  </div>

  <!-- <UAlert v-else-if="error" type="error" class="m-8">
    <template #title>
      Error loading product
    </template>
    <div>
      {{ error.message }}
    </div>
  </UAlert> -->

  <div v-else-if="product" class="mt-8 space-y-12">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        <ProductGallery :images="product.images" :alt="product.name" />

        <!-- Product Details -->
        <div class="space-y-6">
            <!-- Badge Tags -->

          <div class="flex justify-between">
              <div v-if="product.tags?.length > 0" class="flex gap-2">
                <ProductTag v-for="tag in product.tags" :key="tag.name" :tag="tag" />
              </div>

                        <UButton 
              size="lg" 
              variant="soft" 
              color="primary"
              :icon="isInWishlist ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'"
              @click="toggleWishlist"
            />
          </div>
          
  

           <div class="space-y-3">
            <!-- Title -->
            <h1 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100">{{ product.name }}</h1>

             <!-- Rating -->
            <div v-if="product.rating" class="flex items-center gap-3">
              <div class="flex items-center gap-1">
                <Icon name="i-heroicons-star-solid" class="w-5 h-5 text-yellow-400" />
                <span class="font-semibold text-gray-900 dark:text-gray-100">{{ product.rating.average }}</span>
              </div>
              <span class="text-sm text-gray-600 dark:text-gray-400">({{ $t('{n} reviews').replace('[x]', product.rating.count.toString()) }})</span>
              <USeparator orientation="vertical" />
              <NuxtLink to="#reviews" class="text-sm text-primary hover:underline">
                {{ $t('Read reviews') }}
              </NuxtLink>
            </div>

            <p>
              {{ product.shortDescription }}
            </p>

            <PriceCompare class="text-2xl text-primary" :price="product.price" :oldPrice="product.regularPrice"  />
          </div>

          <USeparator  />

          <!-- Product Info -->
          <div class="grid grid-cols-3 gap-4 py-4">
            <div class="text-center">
              <Icon name="i-heroicons-truck" class="w-6 h-6 mx-auto mb-2 text-green-600" />
              <p class="text-xs text-gray-600">{{ $t('Free shipping') }}</p>
            </div>
            <div class="text-center">
              <Icon name="i-heroicons-arrow-path" class="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p class="text-xs text-gray-600">{{ $t('Easy returns') }}</p>
            </div>
            <div class="text-center">
              <Icon name="i-heroicons-shield-check" class="w-6 h-6 mx-auto mb-2 text-purple-600" />
              <p class="text-xs text-gray-600">{{ $t('Secure payment') }}</p>
            </div>
          </div>

          <USeparator  />

          <ProductOptions :options="product.options" :autoselect="true" :variants="product.variants" v-model:variant="productVariant" />

          <!-- <ProductVariantSelector
            label="Size"
            type="buttons"
            :show-help="true"
            :variants="[
              { label: 'S', value: 's', available: true },
              { label: 'M', value: 'm', available: true },
              { label: 'L', value: 'l', available: false },
              { label: 'XL', value: 'xl', available: true }
            ]"
          >
            <template #help>
              <SizeGuide />
            </template>
          </ProductVariantSelector>

           <ProductVariantSelector
            label="Color"
            type="dropdown"
            :variants="[
              { label: 'Red', value: 'red', available: true },
              { label: 'Blue', value: 'blue', available: true },
              { label: 'Green', value: 'green', available: true }
            ]"
          /> -->

          <!-- Product Actions -->

          <ProductDetailsActions :product="product" :variantSelected="productVariant" />

          <USeparator  />

          <!-- Share & Compare -->
          <div class="flex items-center gap-4">
            <UButton variant="ghost" size="sm" color="neutral">
              <Icon name="i-heroicons-share" class="w-4 h-4 mr-2" />
              {{ $t('Share') }}
            </UButton>
            <UButton variant="ghost" size="sm" color="neutral">
              <Icon name="i-heroicons-chart-bar" class="w-4 h-4 mr-2" />
              {{ $t('Compare') }}
            </UButton>
            <UButton variant="ghost" size="sm" color="neutral">
              <Icon name="i-heroicons-flag" class="w-4 h-4 mr-2" />
              {{ $t('Report') }}
            </UButton>
          </div>
        </div>
      </div>

      <ClientOnly>
        <ProductDetailsTabs :product="product" />
      </ClientOnly>

    <RelatedProductSlideshow :productId="product.id" />

    <ProductSlideshow v-if="!recentlyViewedPending && recentlyViewedProducts.length" :title="$t('Recently viewed')"  :products="recentlyViewedProducts" />
  </div>
</template>

<script setup lang="ts">
const { t: $t } = useI18n()

import { useRoute } from 'vue-router'
import ProductGallery from '~/components/molecules/ProductGallery.vue'
import ProductDetailsActions from '~/components/molecules/ProductDetailsActions.vue'
import ProductDetailsTabs from '~/components/organisms/ProductDetailsTabs.vue'
import ProductTag from '~/components/atoms/ProductTag.vue'
import PriceCompare from '~/components/molecules/PriceCompare.vue';
import ProductSlideshow from '~/components/organisms/ProductSlideshow.vue';
import { useWishlistStore } from '~/stores/useWishlistStore'
import { useRecentlyViewedProducts } from '~/composables/useRecentlyViewedProducts'
import RelatedProductSlideshow from '~/components/organisms/RelatedProductSlideshow.vue'
import ProductOptions from '~/components/molecules/ProductOptions.vue'
import type { ProductVariant } from '~/types'
import StoreBreadcrumb from '~/components/organisms/StoreBreadcrumb.vue';
import { useCurrencyStore } from '~/stores/useCurrencyStore'
const wishlist = useWishlistStore()

const route = useRoute()

const currencyStore = useCurrencyStore()
const catalogService = useCatalogService()

const { locale } = useI18n()

const {
  data,
  pending,
  error
} = await useAsyncData(() => catalogService.getProductBySlug(route.params.slug as string), {
  watch: [() => route.params.slug, () => currencyStore.currency, () => locale.value],
  default: () => ({ data: null }),
})


watch(data, (newData) => {
  console.log('Product data loaded:', newData)
}, { immediate: true })


if (error.value) {
  console.error('Error fetching product:', error.value)
  throw createError({ statusCode: 404, statusMessage: 'Product not found' })
}

const product = computed(() => data.value.data)

const {
  data: recentlyViewedProducts,
  pending: recentlyViewedPending,
  addProduct: addRecentlyViewedProduct
} = useRecentlyViewedProducts()

// Use watch to handle both Hydration (immediate) and SPA Navigation (async)
watch(product, (newProduct) => {
  if (newProduct) {
    addRecentlyViewedProduct(newProduct.id)
  }
}, { immediate: true })

const isInWishlist = computed(() => {
  if (!product.value) return false

  return wishlist.isWishlisted(product.value.id)
})

const wishlistItem = computed(() => {
  if (!product.value) return null

  return wishlist.isWishlisted(product.value.id)
})




const toast = useToast()

function toggleWishlist() {
  if (wishlistItem.value) {
    wishlist.removeItem(wishlistItem.value.id)

    toast.add({
      title: 'Removed from wishlist',
      description: product.value?.name,
      color: 'warning'
    })
  } else {
    wishlist.addProduct(product.value!.id)

    toast.add({
      title: 'Added to wishlist',
      description: product.value?.name,
      color: 'success'
    })
  }
}

useHead(() => ({
  title: product.value ? `${product.value.name} - My Shop` : 'Product - My Shop',
  meta: [
    {
      name: 'description',
      content: product.value ? product.value.shortDescription : 'Product details page'
    }
  ]
}))

const productVariant = ref<ProductVariant | null>(null)

const localePath = useLocalePath()

const breadcrumb = computed(() => {
  const items = [
    { label: $t('Home'), to: localePath({ name: 'index' })  },
    { label: $t('Shop'), to: localePath({ name: 'catalog' }) },
  ]

  const categories = getOptimalProductCategoryPath(product.value?.categories ?? [])

  return [
    ...items,
    ...categories.map(category => ({
      label: category.name,
      to: localePath({ name: 'category-slug', params: { slug: category.slug } })
    }))
  ]
})

definePageMeta({
  layout: 'shop',
})
</script>